// ============================================================================
// Move one PO to another transfer order — opened from a block in the Contents
// modal, which is the only place on the board that shows cargo at the PO grain.
//
// Transfers on a lane share ONE transfer order, and everything on that order
// rides ONE truck. When the truck is full, or a PO's goods are not ready to
// leave yet, this takes that PO off and puts it on another transfer — or on a
// brand-new one, which is how you park it until it IS ready.
//
// The destination list is what the OMS says is still open on the same lane; each
// option is labelled by the POs already on it, because that is what the decision
// is actually made on. Whether a move is possible at all is also the server's
// answer (`can_move` / `blocked_reason`) — we render it, we never re-derive it.
// ============================================================================
import { Alert, Modal, Radio, Space, Spin, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";
import {
  getTransferMoveTargets,
  LogisticsCargoAllocation,
  moveTransfer,
  TransferMoveTargets,
} from "../logistics.api";

interface Props {
  allocation: LogisticsCargoAllocation | null; // the PO block whose "Move" was clicked (null → closed)
  onClose: () => void;
  onMoved: () => void; // reload the board + the contents behind us
}

const NEW_ORDER = -1; // the "open a fresh transfer order" choice, kept out of the id space

const OrderOption: React.FC<{ number: string; inZoho: boolean; poNumbers: string[] }> = ({
  number,
  inZoho,
  poNumbers,
}) => (
  <div style={{ padding: "2px 0" }}>
    <Space size={6} wrap>
      <Typography.Text strong>{number}</Typography.Text>
      {!inZoho && <Tag color="gold">Not in Zoho yet</Tag>}
    </Space>
    <div style={{ fontSize: 12, color: "#888" }}>
      {poNumbers.length ? `Carrying ${poNumbers.join(", ")}` : "Nothing on it yet"}
    </div>
  </div>
);

const MoveTransferModal: React.FC<Props> = ({ allocation, onClose, onMoved }) => {
  const [data, setData] = useState<TransferMoveTargets | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState<number>(NEW_ORDER);

  const stageId = allocation?.transfer_stage_id ?? null;

  useEffect(() => {
    if (!stageId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setChoice(NEW_ORDER);
    getTransferMoveTargets(stageId).then((res) => {
      if (cancelled) return;
      setData(res ?? null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [stageId]);

  const submit = async () => {
    if (!stageId) return;
    setSaving(true);
    const ok = await moveTransfer(stageId, choice === NEW_ORDER ? null : choice);
    setSaving(false);
    if (ok) {
      onMoved();
      onClose();
    }
  };

  const blocked = data ? data.blocked_reason : null;

  return (
    <Modal
      title={`Move ${allocation?.po_number || "this PO"} to another transfer order`}
      open={!!allocation}
      onCancel={onClose}
      onOk={submit}
      okText="Move"
      okButtonProps={{ disabled: loading || !!blocked || !data?.can_move, loading: saving }}
      width={560}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
        </div>
      ) : blocked ? (
        <Alert type="warning" showIcon message="This PO cannot be moved" description={blocked} />
      ) : (
        <>
          {data?.current && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Currently on <strong>{data.current.transfer_order_number}</strong>. Moving it also
              takes it off that transfer's truck — the two always travel together.
            </Typography.Paragraph>
          )}
          <Radio.Group
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            style={{ display: "block" }}
          >
            <Space direction="vertical" size={12} style={{ display: "flex" }}>
              <Radio value={NEW_ORDER}>
                <div style={{ padding: "2px 0" }}>
                  <Typography.Text strong>A new transfer order</Typography.Text>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    Opens a fresh transfer with only this PO on it. Later POs on the same lane that
                    are not ready either will join it, and it moves when you book its truck.
                  </div>
                </div>
              </Radio>
              {(data?.targets ?? []).map((t) => (
                <Radio key={t.document_id} value={t.document_id}>
                  <OrderOption
                    number={t.transfer_order_number}
                    inZoho={t.in_zoho}
                    poNumbers={t.po_numbers}
                  />
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          {!data?.targets?.length && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
              No other transfer order on this lane is still open, so a new one is the only
              destination.
            </Typography.Paragraph>
          )}
        </>
      )}
    </Modal>
  );
};

export default MoveTransferModal;
